import {Plural, Trans} from '@lingui/macro';
import {capitalize} from 'lodash';
import React, {FC, Fragment} from 'react';
import styled, {css} from 'styled-components';

import {
  ErrorCodeMap,
  maxErrorCountToShow,
} from '../../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {useLinkNavigation} from '../../../../../../../../../src/components/common/navigation/useLinkNavigation';
import {
  CrmSyncErrorCodesEnum,
  CrmSyncErrorModel,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {Icon} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';

/*
 * Props.
 */

interface CrmSyncsErrorsListProps {
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  errorCodeMap: ErrorCodeMap;
  externalBaseUrl?: string;
}

/*
 * Styles.
 */

const StyledErrorTitleDiv = styled.div`
  display: flex;
  gap: 10px;
  font-weight: ${fontWeights.bold};
  font-size: 14px;
  color: ${(p) => p.theme.greys.shade80};
  margin-bottom: 5px;
`;

const StyledErrorUnorderedList = styled.ul`
  padding: 0 0 0 26px;
  list-style-position: inside;
`;

interface StyledErrorListItemProps {
  $hasExternalUrl: boolean;
}

const StyledErrorListItem = styled.li<StyledErrorListItemProps>`
  margin-top: 8px;
  // negative margin is make some space next to bullet for hover effect
  margin-left: -8px;
  padding: 4px 8px;
  width: fit-content;
  display: flex;
  align-items: center;
  list-style: none;
  gap: 8px;
  ${(p) =>
    p.$hasExternalUrl &&
    css`
      &:hover {
        background-color: ${p.theme.palette.grey.shade10};
        border-radius: 4px;
      }
    `}
`;

const StyledDotSpan = styled.span`
  font-weight: ${fontWeights.bold};
`;

const StyledIcon = styled(Icon)`
  display: none;
  color: ${(p) => p.theme.palette.grey.shade40};
  ${StyledErrorListItem}:hover && {
    display: block;
  }
`;

const StyledErrorsListDiv = styled.div`
  margin-top: 24px;
`;

/*
 * Component.
 */

export const CrmSyncsErrorsList: FC<CrmSyncsErrorsListProps> = (props) => {
  const {errorCodeMap, source, externalBaseUrl, resourceType} = props;
  const config = useCrmPageConfig(source)[resourceType];
  const {navigateToLink} = useLinkNavigation();
  const errorCodes = Object.keys(errorCodeMap);

  if (errorCodes.length === 0) {
    return null;
  }

  const onErrorClick = (externalId?: string) => {
    if (!externalBaseUrl || !externalId || !config) {
      return;
    }

    navigateToLink(config.buildExternalUrl(externalBaseUrl, externalId));
  };
  return (
    <StyledErrorsListDiv>
      {errorCodes.map((errorCode) => {
        const errors = errorCodeMap[errorCode].slice(0, maxErrorCountToShow);
        const errorTitle =
          resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT
            ? computeAccountSyncErrorCodeTitle(errorCode, errorCodeMap[errorCode].length, capitalize(source))
            : computeCardSyncErrorCodeTitle(errorCode, errorCodeMap[errorCode].length, capitalize(source));
        return (
          <Fragment key={errorCode}>
            <UserThemeConsumer>
              {(theme) => (
                <StyledErrorTitleDiv>
                  <Icon color={theme.palette.orange.shade40} name="warningFilledSmall" />
                  <div>{errorTitle}</div>
                </StyledErrorTitleDiv>
              )}
            </UserThemeConsumer>
            <StyledErrorUnorderedList>
              {errors.map((error) => {
                const errorText = getErrorText(errorCode, error);
                return (
                  <StyledErrorListItem
                    key={errorText}
                    $hasExternalUrl={Boolean(externalBaseUrl && error.externalResourceId)}
                    onClick={() => onErrorClick(error.externalResourceId)}
                  >
                    <StyledDotSpan>•</StyledDotSpan>
                    <div>{errorText}</div>
                    {externalBaseUrl && error.externalResourceId && <StyledIcon name="externalLinkSmall" />}
                  </StyledErrorListItem>
                );
              })}
              {maybeRenderMoreIndicator(errorCodeMap[errorCode])}
            </StyledErrorUnorderedList>
          </Fragment>
        );
      })}
    </StyledErrorsListDiv>
  );
};

/*
 * Helpers.
 */

function computeAccountSyncErrorCodeTitle(code: string, numErrors: number, source: string) {
  const numErrorsToDisplay = numErrors > maxErrorCountToShow ? maxErrorCountToShow : numErrors;
  const plusSign = numErrors > maxErrorCountToShow ? '+' : '';

  switch (code) {
    case CrmSyncErrorCodesEnum.INVALID_DOMAIN:
      return (
        <Plural
          id="crm-sync-errors-title-invalid-domain"
          value={numErrorsToDisplay}
          one="# invalid email domain was not synced"
          other={`#${plusSign} invalid email domains were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.COMMON_DOMAIN_USED:
      return (
        <Plural
          id="crm-sync-errors-title-common-domain"
          value={numErrorsToDisplay}
          one="# common email domain was not synced"
          other={`#${plusSign} common email domains were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.NAME_ALREADY_TAKEN:
      return (
        <Plural
          id="crm-sync-errors-title-same-name"
          value={numErrorsToDisplay}
          one="# account is linked to another CRM and was not synced"
          other={`#${plusSign} accounts are linked to another CRM and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.DOMAIN_ALREADY_TAKEN:
      return (
        <Plural
          id="crm-sync-errors-title-domain-taken"
          value={numErrorsToDisplay}
          one="# email domain is already in use and was not synced"
          other={`#${plusSign} email domains are already in use and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.NAME_NOT_FOUND:
      return (
        <Plural
          id="crm-sync-errors-title-name-not-found"
          value={numErrorsToDisplay}
          one={`# ${source} account does not include a name and was not synced`}
          other={`#${plusSign} ${source} accounts do not include a name and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.CRM_FIELD_NOT_FOUND:
      return (
        <Plural
          id="crm-sync-errors-title-field-not-found"
          value={numErrorsToDisplay}
          one={`# mapped ${source} field was not found and was not synced`}
          other={`#${plusSign} mapped ${source} fields were not found and were not synced`}
        />
      );
    default:
      return (
        <Plural
          id="crm-sync-errors-title-unknown"
          value={numErrorsToDisplay}
          one="# account generated unknown errors and was not synced"
          other={`#${plusSign} accounts generated unknown errors and were not synced`}
        />
      );
  }
}

function computeCardSyncErrorCodeTitle(code: string, numErrors: number, source: string) {
  const numErrorsToDisplay = numErrors > maxErrorCountToShow ? maxErrorCountToShow : numErrors;
  const plusSign = numErrors > maxErrorCountToShow ? '+' : '';

  switch (code) {
    case CrmSyncErrorCodesEnum.NAME_ALREADY_TAKEN:
      return (
        <Plural
          id="crm-sync-errors-title-contact-same-name"
          value={numErrorsToDisplay}
          one="# contact is linked to another CRM and was not synced"
          other={`#${plusSign} contacts are linked to another CRM and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.INVALID_HANDLE:
      return (
        <Plural
          id="crm-sync-errors-title-contact-name-not-found"
          value={numErrorsToDisplay}
          one="# contact has an invalid handle"
          other="# contacts have invalid handles"
        />
      );
    case CrmSyncErrorCodesEnum.HANDLE_ALREADY_TAKEN:
      return (
        <Plural
          id="crm-sync-errors-title-contact-handle-taken"
          value={numErrorsToDisplay}
          one="# handle is already taken by another contact and was not synced"
          other={`#${plusSign} handles were already taken by other contacts and were not synced`}
        />
      );
    case CrmSyncErrorCodesEnum.CRM_FIELD_NOT_FOUND:
      return (
        <Plural
          id="crm-sync-errors-title-contact-field-not-found"
          value={numErrorsToDisplay}
          one={`# mapped ${source} field was not found and was not synced`}
          other={`#${plusSign} mapped ${source} fields were not found and were not synced`}
        />
      );
    default:
      return (
        <Plural
          id="crm-sync-errors-title-contact-unknown"
          value={numErrorsToDisplay}
          one="# contact generated unknown errors and was not synced"
          other={`#${plusSign} contacts generated unknown errors and were not synced`}
        />
      );
  }
}

function maybeRenderMoreIndicator(errors: ReadonlyArray<CrmSyncErrorModel>) {
  if (errors.length <= maxErrorCountToShow) {
    return null;
  }
  return (
    <StyledErrorListItem $hasExternalUrl={false}>
      <StyledDotSpan>•</StyledDotSpan>
      <div>
        <Trans id="crm-sync-page-error-details-and-more">and more...</Trans>
      </div>
    </StyledErrorListItem>
  );
}

function getErrorText(code: string, error: CrmSyncErrorModel) {
  switch (code) {
    case CrmSyncErrorCodesEnum.INVALID_DOMAIN:
    case CrmSyncErrorCodesEnum.COMMON_DOMAIN_USED:
    case CrmSyncErrorCodesEnum.DOMAIN_ALREADY_TAKEN:
      return error.externalResourceName ? `${error.reason} (${error.externalResourceName})` : error.reason;
    case CrmSyncErrorCodesEnum.CRM_FIELD_NOT_FOUND:
      return error.externalFieldId;
    case CrmSyncErrorCodesEnum.NAME_NOT_FOUND:
    case CrmSyncErrorCodesEnum.HANDLE_ALREADY_TAKEN:
      return error.externalResourceId;
    case CrmSyncErrorCodesEnum.NAME_ALREADY_TAKEN:
    default:
      return error.externalResourceName;
  }
}
